import React, { useState, useEffect, useMemo, useCallback } from 'react';
import '../css/Filter.css';
import CustomSelect from './CustomSelect';
import { BACKEND_URL } from '../config';
import { CATEGORY_EXTRA_FILTERS, CATEGORIES_WITHOUT_BRAND_MODEL, CATEGORIES_BRAND_ONLY } from '../config/categoryFilters';

const DROPDOWN_MAX_HEIGHT = '200px';
const LOADING_PLACEHOLDER = 'Завантаження...';

const getBrandSlug = (brand) =>
  brand?.slug || brand?.name?.toLowerCase().replace(/\s+/g, '-') || null;

const getModelSlug = (model) =>
  model?.slug || model?.name?.toLowerCase().replace(/\s+/g, '-') || null;

/**
 * Filter - фільтрація продуктів за категорією, маркою, моделлю та ціною.
 *
 * Стан зберігає slugs (не numeric IDs), тому initialFilters передаються
 * напряму з URL-параметрів без очікування slug-resolution у батьківському компоненті.
 *
 * @param {Function} onApplyFilter - Callback: { brand, model, category, minPrice, maxPrice, inStock }
 * @param {Object}   initialFilters - { brand, model, category, minPrice, maxPrice, inStock } (slugs)
 */
const Filter = ({ onApplyFilter, initialFilters = {} }) => {
  // ============================================================================
  // STATE
  // ============================================================================

  const [selectedBrand,    setSelectedBrand]    = useState('');
  const [selectedModel,    setSelectedModel]    = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [priceFrom,        setPriceFrom]        = useState('');
  const [priceTo,          setPriceTo]          = useState('');
  const [inStock,          setInStock]          = useState(false);

  const [brands,     setBrands]     = useState([]);
  const [models,     setModels]     = useState([]);
  const [categories, setCategories] = useState([]);

  const [loadingBrands,     setLoadingBrands]     = useState(false);
  const [loadingModels,     setLoadingModels]     = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);

  /** Значення extra-фільтрів поточної категорії (e.g. { manufacturer: 'Denso' }) */
  const [extraValues,  setExtraValues]  = useState({});
  /** Підгружені варіанти для extra select-полів (e.g. { manufacturer: ['Denso', 'Sanden'] }) */
  const [extraOptions, setExtraOptions] = useState({});

  // ============================================================================
  // HELPERS
  // ============================================================================

  const fetchData = useCallback(async (url, onSuccess, setLoading) => {
    setLoading(true);
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      onSuccess(await response.json());
    } catch (error) {
      console.error(`Error fetching data from ${url}:`, error);
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Завантаження брендів і категорій при монтуванні
  useEffect(() => {
    fetchData(`${BACKEND_URL}/categories/brands`, setBrands,     setLoadingBrands);
    fetchData(`${BACKEND_URL}/categories`,         setCategories, setLoadingCategories);
  }, [fetchData]);

  // Ініціалізація стану зі slug-based initialFilters.
  // Не викликає onApplyFilter — тільки синхронізує відображення з URL.
  useEffect(() => {
    setSelectedBrand   (initialFilters.brand    || '');
    setSelectedModel   (initialFilters.model    || '');
    setSelectedCategory(initialFilters.category || '');
    setPriceFrom       (initialFilters.minPrice || '');
    setPriceTo         (initialFilters.maxPrice || '');
    setInStock         (initialFilters.inStock  || false);

    // Ініціалізуємо extra-значення (напр. manufacturer) з URL
    const knownKeys = ['brand', 'model', 'category', 'minPrice', 'maxPrice', 'inStock', 'year', 'engine'];
    const extraFromUrl = Object.fromEntries(
      Object.entries(initialFilters).filter(([k, v]) => !knownKeys.includes(k) && v)
    );
    if (Object.keys(extraFromUrl).length > 0) {
      setExtraValues(extraFromUrl);
    }
  }, [
    initialFilters.brand,
    initialFilters.model,
    initialFilters.category,
    initialFilters.minPrice,
    initialFilters.maxPrice,
    initialFilters.inStock,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    initialFilters.manufacturer,
  ]);

  // Завантаження extra-опцій при зміні категорії
  useEffect(() => {
    setExtraOptions({});

    const extraFields = CATEGORY_EXTRA_FILTERS[selectedCategory] || [];
    if (!extraFields.length || !categories.length) return;

    const categoryObj = categories.find(c => c.slug === selectedCategory);
    if (!categoryObj) return;

    const controller = new AbortController();

    extraFields.forEach(field => {
      if (field.source === 'manufacturers') {
        fetch(`${BACKEND_URL}/products/manufacturers?categoryId=${categoryObj.id}`, { signal: controller.signal })
          .then(r => r.ok ? r.json() : [])
          .then(data => {
            setExtraOptions(prev => ({ ...prev, [field.key]: data || [] }));
          })
          .catch(err => {
            if (err.name !== 'AbortError') console.error('Error fetching manufacturers:', err);
          });
      }
    });

    return () => controller.abort();
  }, [selectedCategory, categories]);

  // Завантаження моделей при зміні бренду.
  // brands — dep, щоб повторити спробу після їх завантаження.
  useEffect(() => {
    if (!selectedBrand || !brands.length) {
      setModels([]);
      return;
    }
    const brandObj = brands.find(b => getBrandSlug(b) === selectedBrand);
    if (!brandObj) {
      setModels([]);
      return;
    }
    fetchData(
      `${BACKEND_URL}/categories/brands/${brandObj.id}/models`,
      setModels,
      setLoadingModels
    );
  }, [selectedBrand, brands, fetchData]);

  // ============================================================================
  // HELPERS
  // ============================================================================

  /** Будує повний об'єкт фільтрів з поточного стану + опціональними overrides */
  const buildPayload = useCallback((overrides = {}) => ({
    brand:    selectedBrand    || null,
    model:    selectedModel    || null,
    category: selectedCategory || null,
    minPrice: priceFrom        || null,
    maxPrice: priceTo          || null,
    inStock:  inStock          || null,
    ...extraValues,
    ...overrides,
  }), [selectedBrand, selectedModel, selectedCategory, priceFrom, priceTo, inStock, extraValues]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleCategoryChange = useCallback((e) => {
    const value = e.target.value;
    setSelectedCategory(value);
    setExtraValues({});
    // Явно обнуляємо всі ключі попередніх extra-фільтрів, щоб вони не потрапили в URL
    const extraNulls = Object.fromEntries(Object.keys(extraValues).map(k => [k, null]));
    if (CATEGORIES_WITHOUT_BRAND_MODEL.has(value)) {
      setSelectedBrand('');
      setSelectedModel('');
      onApplyFilter(buildPayload({ category: value || null, brand: null, model: null, ...extraNulls }));
    } else if (CATEGORIES_BRAND_ONLY.has(value)) {
      setSelectedModel('');
      onApplyFilter(buildPayload({ category: value || null, model: null, ...extraNulls }));
    } else {
      onApplyFilter(buildPayload({ category: value || null, ...extraNulls }));
    }
  }, [buildPayload, onApplyFilter, extraValues]);

  const handleBrandChange = useCallback((e) => {
    const value = e.target.value;
    setSelectedBrand(value);
    setSelectedModel('');
    onApplyFilter(buildPayload({ brand: value || null, model: null }));
  }, [buildPayload, onApplyFilter]);

  const handleModelChange = useCallback((e) => {
    const value = e.target.value;
    setSelectedModel(value);
    onApplyFilter(buildPayload({ model: value || null }));
  }, [buildPayload, onApplyFilter]);

  const handleApplyClick = useCallback(() => {
    onApplyFilter(buildPayload());
  }, [buildPayload, onApplyFilter]);

  const handleReset = useCallback(() => {
    setSelectedBrand('');
    setSelectedModel('');
    setSelectedCategory('');
    setPriceFrom('');
    setPriceTo('');
    setInStock(false);
    setExtraValues({});
    onApplyFilter({
      brand: null, model: null, category: null,
      minPrice: null, maxPrice: null, inStock: null,
    });
  }, [onApplyFilter]);

  // ============================================================================
  // MEMOIZED OPTIONS (значення — slugs)
  // ============================================================================

  const categoryOptions = useMemo(
    () => categories.map(cat => ({ value: cat.slug, label: cat.name })),
    [categories]
  );

  const brandOptions = useMemo(
    () => brands.map(brand => ({ value: getBrandSlug(brand), label: brand.name })),
    [brands]
  );

  const modelOptions = useMemo(
    () => models.map(model => ({ value: getModelSlug(model), label: model.name })),
    [models]
  );

  const modelPlaceholder = useMemo(() => {
    if (!selectedBrand) return 'Оберіть марку';
    if (loadingModels)  return LOADING_PLACEHOLDER;
    return 'Усі моделі';
  }, [selectedBrand, loadingModels]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="filter-container">
      <h2 className="filter-title">Фільтри</h2>

      <div className="filter-input-group">
        <label className="filter-label">Категорія</label>
        <CustomSelect
          value={selectedCategory}
          onChange={handleCategoryChange}
          disabled={loadingCategories}
          placeholder={loadingCategories ? LOADING_PLACEHOLDER : 'Усі категорії'}
          options={categoryOptions}
          maxHeight={DROPDOWN_MAX_HEIGHT}
        />
      </div>

      {selectedCategory && !CATEGORIES_WITHOUT_BRAND_MODEL.has(selectedCategory) && (
        <div className="filter-input-group">
          <label className="filter-label">Марка авто</label>
          <CustomSelect
            value={selectedBrand}
            onChange={handleBrandChange}
            disabled={loadingBrands}
            placeholder={loadingBrands ? LOADING_PLACEHOLDER : 'Усі марки'}
            options={brandOptions}
            maxHeight={DROPDOWN_MAX_HEIGHT}
          />
        </div>
      )}

      {selectedCategory
        && !CATEGORIES_WITHOUT_BRAND_MODEL.has(selectedCategory)
        && !CATEGORIES_BRAND_ONLY.has(selectedCategory) && (
        <div className="filter-input-group">
          <label className="filter-label">Модель авто</label>
          <CustomSelect
            value={selectedModel}
            onChange={handleModelChange}
            disabled={!selectedBrand || loadingModels}
            placeholder={modelPlaceholder}
            options={modelOptions}
            maxHeight={DROPDOWN_MAX_HEIGHT}
          />
        </div>
      )}

      {(CATEGORY_EXTRA_FILTERS[selectedCategory] || []).map(field => (
        <div key={field.key} className="filter-input-group">
          <label className="filter-label">{field.label}</label>
          <CustomSelect
            value={extraValues[field.key] || ''}
            onChange={(e) => {
              const val = e.target.value;
              setExtraValues(prev => ({ ...prev, [field.key]: val }));
              onApplyFilter(buildPayload({ [field.key]: val || null }));
            }}
            options={(extraOptions[field.key] || []).map(v => ({ value: v, label: v }))}
            placeholder={field.placeholder || `Усі`}
            maxHeight={DROPDOWN_MAX_HEIGHT}
          />
        </div>
      ))}

      <div className="filter-divider"></div>

      <div className="filter-grid">
        <div className="filter-input-group">
          <label className="filter-label">Ціна від</label>
          <input
            type="number"
            value={priceFrom}
            onChange={(e) => setPriceFrom(e.target.value)}
            className="filter-input"
            placeholder="0"
            min="0"
          />
        </div>

        <div className="filter-input-group">
          <label className="filter-label">Ціна до</label>
          <input
            type="number"
            value={priceTo}
            onChange={(e) => setPriceTo(e.target.value)}
            className="filter-input"
            placeholder="999999"
            min="0"
          />
        </div>
      </div>

      <div className="filter-checkbox-group">
        <input
          type="checkbox"
          id="inStock"
          checked={inStock}
          onChange={(e) => setInStock(e.target.checked)}
          className="filter-checkbox"
        />
        <label htmlFor="inStock" className="filter-checkbox-label">
          В наявності
        </label>
      </div>

      <div className="filter-actions">
        <button
          onClick={handleApplyClick}
          className="filter-button filter-button-primary"
          type="button"
        >
          Застосувати
        </button>
        <button
          onClick={handleReset}
          className="filter-button filter-button-secondary"
          type="button"
        >
          Скинути
        </button>
      </div>
    </div>
  );
};

export default Filter;
